import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { ShieldWarning, ArrowClockwise, CheckCircle, ArrowUUpLeft, Pause, LockOpen, CircleNotch } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/sonner";
import { NavTabs } from "@/components/NavTabs";
import { Footer } from "@/components/Footer";
import { formatUnit } from "../lib/formatUnit";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (v) => (v == null ? "—" : Number(v).toLocaleString("en-IN", { maximumFractionDigits: 4 }));
const when = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

const STATUS_TONE = {
  pending: "bg-[#FFFAEB] text-[#B54708] border-[#FEDF89]",
  rework: "bg-[#FEF3F2] text-[#B42318] border-[#FECDCA]",
  hold: "bg-[#F2F4F7] text-[#475467] border-[#D0D5DD]",
  approved: "bg-[#ECFDF3] text-[#027A48] border-[#ABEFC6]",
  posted: "bg-[#EFF8FF] text-[#026AA2] border-[#B2DDFF]",
};
const STATUS_LABEL = {
  pending: "Pending Quality Decision",
  rework: "Rework - Lot Blocked",
  hold: "On Hold - Lot Blocked",
  approved: "Cleared - Production To Post",
  posted: "Posted To SAP",
};

// Oct 27 2026, user's spec point 5: quality views the notes, enters the
// Physical Accepted Quantity, approves the scrap, sends it to rework, keeps a
// lot on hold or releases it. Only models the Admin assigned Quality Activity
// to ever raise a note, so nothing else can appear here.
const NoteCard = ({ note, canDecide, onDecided }) => {
  const requested = note.requested || {};
  const [accepted, setAccepted] = useState(String(requested.confirmed_quantity ?? ""));
  const [scrap, setScrap] = useState(String(requested.confirmed_scrap ?? ""));
  const [remark, setRemark] = useState("");
  const [busy, setBusy] = useState("");
  const blocked = ["pending", "rework", "hold"].includes(note.status);

  const decide = async (outcome) => {
    setBusy(outcome);
    try {
      const { data } = await axios.post(`${API}/quality-decisions/${note._id}/decide`, {
        outcome,
        physical_accepted_quantity: Number(accepted),
        approved_scrap_quantity: Number(scrap),
        remark: remark.trim(),
      });
      const done = {
        approve: "Scrap approved - production can post the cleared figures",
        release: "Lot released - production can post the cleared figures",
        rework: "Sent for rework - the lot stays blocked",
        hold: "Lot kept on hold - it stays blocked",
      }[outcome];
      toast.success(`${note._id}: ${done}`);
      onDecided(data);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not record that decision");
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="bg-white border border-[#D0D5DD] rounded-sm" data-testid={`qdn-card-${note._id}`}>
      <div className="px-4 py-3 border-b border-[#EAECF0] flex items-center gap-3 flex-wrap">
        <span className="font-mono font-bold text-[#175CD3]">{note._id}</span>
        <Badge variant="outline" className={`text-[10px] ${STATUS_TONE[note.status] || ""}`} data-testid={`qdn-status-${note._id}`}>
          {STATUS_LABEL[note.status] || note.status}
        </Badge>
        <span className="text-xs text-[#667085]">
          Lot <span className="font-mono font-bold text-[#101828]">{note.production_lot_id}</span> · {note.main_output_product || "—"} ·{" "}
          {note.reporting_point_description || note.reporting_point_id || "—"} · site {note.site_id || "—"}
        </span>
        <span className="text-xs text-[#98A2B3] ml-auto">Raised by {note.raised_by} · {when(note.raised_at)}</span>
      </div>

      <div className="p-4 grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-4">
        <div className="space-y-2 text-[13px]">
          <h3 className="text-xs font-bold text-[#344054] uppercase tracking-wide">What production reported</h3>
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-[#F9FAFB] border border-[#EAECF0] rounded-sm px-2.5 py-1.5">
              <div className="text-[11px] text-[#667085]">Confirmed Qty</div>
              <div className="font-bold tabular-nums">{fmt(requested.confirmed_quantity)} {formatUnit(note.unit_code)}</div>
            </div>
            <div className="bg-[#FFFAEB] border border-[#FEDF89] rounded-sm px-2.5 py-1.5">
              <div className="text-[11px] text-[#B54708]">Scrap Reported</div>
              <div className="font-bold tabular-nums text-[#B54708]" data-testid={`qdn-scrap-${note._id}`}>
                {fmt(requested.confirmed_scrap)} {formatUnit(note.unit_code)}
              </div>
            </div>
          </div>
          <div className="text-[12px] text-[#475467]">
            Scrap reason: <span className="font-bold">{requested.deviation_reason_code || "—"}</span>
          </div>
          <div className="text-[12px] text-[#475467]">
            Production model: <span className="font-mono">{note.production_model_id}</span>
          </div>
          {note.decision && (
            <div
              className={`text-[12px] rounded-sm px-2.5 py-1.5 border ${
                blocked
                  ? "text-[#B54708] bg-[#FFFAEB] border-[#FEDF89]"
                  : "text-[#027A48] bg-[#ECFDF3] border-[#ABEFC6]"
              }`}
              data-testid={`qdn-decision-${note._id}`}
            >
              {note.decision.outcome} by {note.decision.decided_by} · {when(note.decision.decided_at)}
              {note.decision.physical_accepted_quantity != null && (
                <> · accepted {fmt(note.decision.physical_accepted_quantity)}, scrap {fmt(note.decision.approved_scrap_quantity)}</>
              )}
              {note.decision.remark ? <div className="text-[#475467]">"{note.decision.remark}"</div> : null}
            </div>
          )}
        </div>

        {canDecide && blocked && (
          <div className="space-y-3">
            <h3 className="text-xs font-bold text-[#344054] uppercase tracking-wide">Quality decision</h3>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-xs font-bold text-[#344054]">Physical Accepted Qty *</Label>
                <Input
                  type="number" min="0" step="any" value={accepted}
                  onChange={(e) => setAccepted(e.target.value)}
                  className="h-9 text-right tabular-nums"
                  data-testid={`qdn-accepted-input-${note._id}`}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-bold text-[#344054]">Approved Scrap Qty</Label>
                <Input
                  type="number" min="0" step="any" value={scrap}
                  onChange={(e) => setScrap(e.target.value)}
                  className="h-9 text-right tabular-nums"
                  data-testid={`qdn-scrap-input-${note._id}`}
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-bold text-[#344054]">Remark {note.status === "pending" ? "" : "*"}</Label>
              <Textarea
                value={remark} rows={2}
                onChange={(e) => setRemark(e.target.value)}
                placeholder="What production should know - needed for rework or hold"
                data-testid={`qdn-remark-input-${note._id}`}
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              {note.status === "pending" ? (
                <Button className="h-9" disabled={!!busy} onClick={() => decide("approve")} data-testid={`qdn-approve-${note._id}`}>
                  <CheckCircle size={14} weight="bold" className="mr-1.5" />
                  {busy === "approve" ? "Approving..." : "Approve Scrap"}
                </Button>
              ) : (
                <Button className="h-9" disabled={!!busy} onClick={() => decide("release")} data-testid={`qdn-release-${note._id}`}>
                  <LockOpen size={14} weight="bold" className="mr-1.5" />
                  {busy === "release" ? "Releasing..." : "Release Lot"}
                </Button>
              )}
              <Button
                variant="outline" className="h-9 bg-white" disabled={!!busy}
                onClick={() => decide("rework")} data-testid={`qdn-rework-${note._id}`}
              >
                <ArrowUUpLeft size={14} weight="bold" className="mr-1.5" /> Rework
              </Button>
              <Button
                variant="outline" className="h-9 bg-white" disabled={!!busy}
                onClick={() => decide("hold")} data-testid={`qdn-hold-${note._id}`}
              >
                <Pause size={14} weight="bold" className="mr-1.5" /> Hold
              </Button>
            </div>
            <p className="text-[11px] text-[#667085]">
              Approving does not post anything to SAP - production confirms the cleared figures from their own screen.
            </p>
          </div>
        )}
        {!canDecide && blocked && (
          <div className="text-[12px] text-[#667085] self-center" data-testid={`qdn-readonly-${note._id}`}>
            Waiting on the quality team - you can see this note but not decide it.
          </div>
        )}
      </div>
    </div>
  );
};

export default function QualityDecisionPage() {
  const [notes, setNotes] = useState([]);
  const [canDecide, setCanDecide] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(async (all) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/quality-decisions`, { params: { status: all ? "" : "open" } });
      setNotes(data.notes || []);
      setCanDecide(!!data.can_decide);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not read the Quality Decision Notes");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(showAll); }, [load, showAll]);

  const onDecided = (updated) => setNotes((prev) => prev.map((n) => (n._id === updated._id ? updated : n)));

  return (
    <div className="min-h-screen bg-[#F9FAFB] flex flex-col">
      <header className="min-h-16 bg-[#0E7C86] shadow-[0_1px_3px_0_rgba(16,24,40,0.15)] flex items-center justify-between px-3 sm:px-5 shrink-0 z-10 gap-2 sm:gap-4">
        <div className="flex items-center gap-3 shrink-0" data-testid="app-title">
          <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
            <ShieldWarning size={18} weight="fill" className="text-white" />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="font-heading text-[16px] font-bold text-white tracking-tight">Production Control</span>
            <span className="font-sans text-[12px] text-white/70 hidden sm:inline">Quality Decision</span>
          </div>
        </div>
        <div className="w-px h-7 bg-white/25 shrink-0" />
        <div className="flex items-center gap-3 flex-1 justify-start min-w-0">
          <NavTabs />
        </div>
      </header>
      <main className="flex-1 max-w-[1280px] w-full mx-auto px-4 py-6 space-y-4" data-testid="quality-decision-page">
        <div className="flex items-center gap-3 flex-wrap">
          <ShieldWarning size={20} className="text-[#0E7C86]" weight="fill" />
          <h1 className="font-heading text-lg font-bold text-[#101828]">Quality Decision</h1>
          <span className="text-sm text-[#475467]">
            Scrap reported on a production model with Quality Activity waits here - the lot stays blocked until you decide
          </span>
          <div className="ml-auto flex gap-2">
            <Button variant="outline" size="sm" className="h-8 bg-white" onClick={() => setShowAll((v) => !v)} data-testid="qdn-toggle-all">
              {showAll ? "Show open only" : "Show all notes"}
            </Button>
            <Button variant="outline" size="sm" className="h-8 bg-white" onClick={() => load(showAll)} data-testid="qdn-refresh">
              <ArrowClockwise size={14} className="mr-1.5" /> Refresh
            </Button>
          </div>
        </div>

        {loading && <div className="py-12 text-center text-[#98A2B3]"><CircleNotch size={20} className="animate-spin inline" /></div>}
        {!loading && notes.length === 0 && (
          <div className="bg-white border border-[#D0D5DD] rounded-sm py-12 text-center text-[#667085]" data-testid="qdn-empty">
            No Quality Decision Notes {showAll ? "at all" : "waiting"} - nothing is blocked.
          </div>
        )}
        {notes.map((note) => (
          <NoteCard key={note._id} note={note} canDecide={canDecide} onDecided={onDecided} />
        ))}
      </main>
      <Footer />
    </div>
  );
}
